// WishlistSystem class for handling the wishlist button functionality
class WishlistSystem {
  constructor() {
    this.buttons = document.querySelectorAll('.wishlist-heart-button');
    this.wishlist = new Set(this.getWishlist());
    this.wishlistCount = document.querySelector('.wishlist-count');
    this.wishlistCountNumber = this.wishlistCount?.querySelector('[aria-hidden="true"]');
    this.init();
    this.updateWishlistCount(); // Update count on initialization
  }

  init() {
    this.buttons.forEach(button => {
      const productHandle = button.dataset.productHandle;
      if (this.wishlist.has(productHandle)) {
        button.classList.add('active');
      }
      button.addEventListener('click', (e) => this.toggleWishlist(e));
    });
  }

  updateWishlistCount() {
    if (this.wishlistCount && this.wishlistCountNumber) {
      const count = this.wishlist.size;
      this.wishlistCountNumber.textContent = count;
      this.wishlistCount.classList.toggle('opacity-0', count === 0);
    }
  }

  toggleWishlist(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const productHandle = button.dataset.productHandle;
    
    if (this.wishlist.has(productHandle)) {
      this.wishlist.delete(productHandle);
      button.classList.remove('active');
      this.showNotification(false);
    } else {
      this.wishlist.add(productHandle);
      button.classList.add('active');
      this.showNotification(true);
    }
    
    this.saveWishlist();
    this.updateWishlistCount(); // Update count after toggle
  }

  getWishlist() {
    const wishlist = localStorage.getItem('wishlist');
    return wishlist ? JSON.parse(wishlist) : [];
  }

  saveWishlist() {
    localStorage.setItem('wishlist', JSON.stringify([...this.wishlist]));
  }

  showNotification(isAdded) {
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = isAdded ? 'Added to wishlist!' : 'Removed from wishlist';
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  }
}

// WishlistPageHandler class for handling the wishlist page functionality
class WishlistPageHandler {
  constructor() {
    if (window.location.pathname.includes('/pages/wishlist')) {
      this.container = document.querySelector('[data-wishlist-container]');
      this.emptyMessage = document.querySelector('.empty-wishlist');
      this.wishlistCount = document.querySelector('.wishlist-count');
      this.wishlistCountNumber = this.wishlistCount?.querySelector('[aria-hidden="true"]');
      if (this.container) {
        this.init();
      }
    }
  }

  init() {
    this.renderWishlist();
    this.setupEventListeners();
  }
  
  getWishlist() {
    const wishlist = localStorage.getItem('wishlist');
    return wishlist ? JSON.parse(wishlist) : [];
  }

  async renderWishlist() {
    const wishlist = this.getWishlist();
    
    if (wishlist.length === 0) {
      this.container.style.display = 'none';
      this.emptyMessage.style.display = 'block';
      return;
    }

    this.container.style.display = 'grid';
    this.emptyMessage.style.display = 'none';
    
    try {
      const products = await Promise.all(
        wishlist.map(async (productHandle) => {
          try {
            const response = await fetch(`/products/${productHandle}.js`);
            if (!response.ok) throw new Error('Product not found');
            const product = await response.json();
            
            return {
              title: product.title,
              price: product.price,
              featured_image: product.featured_image,
              handle: product.handle,
              variant_id: product.variants[0].id
            };
          } catch (error) {
            console.error(`Error fetching product ${productHandle}:`, error);
            return null;
          }
        })
      );

      const validProducts = products.filter(p => p !== null);

      if (validProducts.length === 0) {
        this.container.style.display = 'none';
        this.emptyMessage.style.display = 'block';
        return;
      }

      this.container.innerHTML = validProducts.map(product => `
  <div class="wishlist-item card-border" data-product-handle="${product.handle}">
    <div class="wishlist-item__image-wrapper">
      <img src="${product.featured_image}" alt="${product.title}" class="wishlist-item__image">
    </div>
    <div class="wishlist-item__content">
      <h3 class="wishlist-item__title h4">${product.title}</h3>
      <p class="wishlist-item__price text-size-medium">BHD ${(product.price/100).toFixed(3)}</p>
      <div class="wishlist-item__buttons">
        <a href="/products/${product.handle}" class="button button--primary">View Product</a>
        <button class="button button--secondary" id="remove-from-wishlist" data-product-handle="${product.handle}">
          <span class="remove-text">Remove</span>
        </button>
      </div>
    </div>
  </div>
`).join('');

    } catch (error) {
      console.error('Error loading wishlist products:', error);
      this.container.innerHTML = '<p>Error loading wishlist products. Please try again later.</p>';
    }
  }

  setupEventListeners() {
  this.container.addEventListener('click', (e) => {
    if (e.target.id === 'remove-from-wishlist') {
      const productHandle = e.target.dataset.productHandle;
      this.removeFromWishlist(productHandle);
    }
  });
}

 removeFromWishlist(productHandle) {
    const wishlist = this.getWishlist();
    const updatedWishlist = wishlist.filter(handle => handle !== productHandle);
    localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
    
    // Update the count in the header
    if (this.wishlistCount && this.wishlistCountNumber) {
      const count = updatedWishlist.length;
      this.wishlistCountNumber.textContent = count;
      this.wishlistCount.classList.toggle('opacity-0', count === 0);
    }

    this.renderWishlist();
  }
}

// Styles remain the same...
const styles = `
  /* Wishlist Page Styles */
[data-wishlist-container] {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
}

.empty-wishlist {
  text-align: center;
  padding: 4rem 2rem;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 2rem 0;
}

.empty-wishlist h2 {
  color: #052D67;
  margin-bottom: 1rem;
}

.wishlist-item {
  position: relative;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.wishlist-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.wishlist-item__image-wrapper {
  position: relative;
  padding-top: 100%;
  overflow: hidden;
  background: #f5f5f5;
}

.wishlist-item__image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.wishlist-item:hover .wishlist-item__image {
  transform: scale(1.05);
}

.wishlist-item__content {
  padding: 1.5rem;
}

.wishlist-item__title {
  color: #052D67;
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.4;
}

.wishlist-item__price {
  color: #052D67;
  font-weight: 500;
  margin-bottom: 1rem;
}

.wishlist-item__buttons {
  display: flex;
  gap: 1rem;
  flex-direction: column;
}


/* Responsive adjustments */
@media (max-width: 768px) {
  [data-wishlist-container] {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
    padding: 1rem;
  }

  .wishlist-item__content {
    padding: 1rem;
  }
}

/* Animation for removing items */
.wishlist-item.removing {
  animation: fadeOut 0.3s ease forwards;
}

@keyframes fadeOut {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.95); }
}

/* Notification styles */
.wishlist-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #052D67;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

`;

// Add styles to document
const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

// Initialize both systems when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new WishlistSystem(); // This will now run on every page
  if (window.location.pathname.includes('/pages/wishlist')) {
    new WishlistPageHandler();
  }
});

